const STORAGE_PREFIX = "updates-required";

// Used when a round identifier isn't available (e.g. a stale backend that
// hasn't deployed requestedAt yet) — degrades to the old, round-unaware
// behavior rather than crashing.
const UNKNOWN_ROUND = "unknown";

function baselineKey(
  investorId: string,
  itemKey: string,
  round: string,
): string {
  return `${STORAGE_PREFIX}:${investorId}:${itemKey}:${round}:baseline`;
}

function resolvedKey(
  investorId: string,
  itemKey: string,
  round: string,
): string {
  return `${STORAGE_PREFIX}:${investorId}:${itemKey}:${round}:resolved`;
}

/**
 * Tracks whether an operator-flagged item (an Entity Information field, or a
 * Key Party) has been resolved by the investor. The backend has no persisted
 * "resolved" state for these two item types (unlike documents, which
 * self-resolve via a new documentId on re-upload) — the flagged comment stays
 * in place until the operator reviews again. So resolution here is a
 * session-scoped, client-only approximation: the first time an item is seen,
 * its current value is captured as a baseline; from then on, any value that
 * differs from that baseline marks the item resolved.
 *
 * Detecting a change requires the item's live current value, which is only
 * available where that field/record is actually rendered (e.g. inside the
 * Entity Information form) — but OTHER places that need to know whether an
 * item is resolved (Submit gating, tab badges) don't have that live value
 * and may not even be mounted at the same time. So resolution is split in
 * two: `markPotentiallyResolved` does the detection (call it wherever the
 * live value is available) and persists a plain resolved flag;
 * `isItemResolved` is a pure read of that flag, safe to call from anywhere.
 *
 * `roundId` (the requested-updates round's `requestedAt`, from
 * `RequestedUpdatesGrouped.requestedAt`) is folded into the storage key so
 * resolution is scoped to the specific round it happened in. Without this, an
 * item resolved in one round would stay silently resolved forever, even after
 * the operator opens a brand-new round re-flagging the same field/key party
 * with a different comment — the storage key is otherwise identical, so
 * "already resolved" would falsely apply to unrelated, unreviewed feedback.
 *
 * sessionStorage (not localStorage) is deliberate: it survives in-app tab
 * navigation and same-tab reloads within one browser session, but is cleared
 * on tab close and never leaks across sessions/devices — appropriate given
 * this has no real authority over the item's actual state (only the
 * operator's next review does).
 */
export function markPotentiallyResolved(
  investorId: string,
  itemKey: string,
  currentValue: string,
  roundId: string | undefined,
): boolean {
  const round = roundId ?? UNKNOWN_ROUND;

  try {
    if (
      window.sessionStorage.getItem(resolvedKey(investorId, itemKey, round))
    ) {
      return true;
    }

    const baseline = window.sessionStorage.getItem(
      baselineKey(investorId, itemKey, round),
    );

    if (baseline === null) {
      window.sessionStorage.setItem(
        baselineKey(investorId, itemKey, round),
        currentValue,
      );
      return false;
    }

    if (baseline !== currentValue) {
      window.sessionStorage.setItem(
        resolvedKey(investorId, itemKey, round),
        "true",
      );
      return true;
    }

    return false;
  } catch {
    // sessionStorage unavailable (private browsing, disabled storage, etc.) —
    // fail closed: treat as not yet resolved rather than throwing.
    return false;
  }
}

/** Pure read of the resolved flag — safe to call without the item's live value. */
export function isItemResolved(
  investorId: string,
  itemKey: string,
  roundId: string | undefined,
): boolean {
  const round = roundId ?? UNKNOWN_ROUND;

  try {
    return !!window.sessionStorage.getItem(
      resolvedKey(investorId, itemKey, round),
    );
  } catch {
    return false;
  }
}
